import type { CardTemplateId } from '@shared-core/features/backend'
import { CARD_BLOCK_OTP_LENGTH, useCardBlock } from '@shared-core/hooks/use-card-block'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { AppBankCard } from '@shared-ui/components/app/bank-card'
import { AppConfirmCodeInput, type ConfirmCodeInputRef } from '@shared-ui/components/app/confirm-code-input'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { Button } from '@shared-ui/components/ui/button'
import { Checkbox } from '@shared-ui/components/ui/checkbox'
import { Column } from '@shared-ui/components/ui/grid'
import { GText } from '@shared-ui/components/ui/text'
import { useWideDesktopLayout } from '@shared-ui/hooks/use-wide-desktop-layout'
import { useEffect, useRef, useState } from 'react'
import { KeyboardAvoidingView, Modal, Platform, Pressable, ScrollView } from 'react-native'
import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

export type AppCardBlockSheetProps = {
  cardId: string
  type: CardTemplateId
  isVisible: boolean
  status?: string
  onClose: () => void
  testID?: string
}

export function AppCardBlockSheet({ cardId, type, isVisible, status, onClose, testID }: AppCardBlockSheetProps) {
  const styles = useCreateStyles()
  const isDesktop = useWideDesktopLayout()
  const { t } = useI18N(locales)
  const [accepted, setAccepted] = useState(false)
  const [code, setCode] = useState('')
  const flow = useCardBlock(cardId, status)
  const codeInputRef = useRef<ConfirmCodeInputRef>(null)
  const codeLength = CARD_BLOCK_OTP_LENGTH
  useEffect(() => {
    if (isVisible && flow.preparation) codeInputRef.current?.focus()
  }, [isVisible, flow.preparation])
  const isBlocked = status === 'blocked' || status === 'blocking'
  useEffect(() => {
    if (!isVisible) {
      setAccepted(false)
      setCode('')
      flow.reset()
    }
  }, [isVisible, flow.reset])
  const close = onClose
  return (
    <Modal visible={isVisible} transparent animationType="fade" onRequestClose={close} statusBarTranslucent>
      <KeyboardAvoidingView
        style={[styles.overlay, isDesktop && styles.overlayDesktop]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <Pressable style={styles.backdrop} onPress={close} accessibilityLabel={t('close')} />
        <ScrollView
          style={[styles.sheet, isDesktop && styles.sheetDesktop]}
          contentContainerStyle={styles.content}
          testID={testID}
          keyboardShouldPersistTaps="handled"
        >
          <Pressable style={styles.close} onPress={close} accessibilityRole="button" accessibilityLabel={t('close')}>
            <AppIcon icon="close" size={24} />
          </Pressable>
          {isBlocked && (
            <Column align="center" gap={32} style={styles.statusContent}>
              <AppBankCard type={type} size="middle" isBlocked isNumberVisible={false} />
              <GText style={styles.status}>{t(status === 'blocking' ? 'blocking' : 'blocked')}</GText>
            </Column>
          )}
          {!isBlocked && (
            <Column gap={32}>
              <Column gap={16}>
                <GText fw={600} style={styles.title}>
                  {t(flow.preparation ? 'code-title' : 'title')}
                </GText>
                <GText style={styles.description}>{t(flow.preparation ? 'code-description' : 'description')}</GText>
              </Column>
              <Column gap={16}>
                {flow.preparation && (
                  <AppConfirmCodeInput
                    ref={codeInputRef}
                    value={code}
                    length={codeLength}
                    onChange={(value) => {
                      if (!flow.isLoading) setCode(value.substring(0, codeLength))
                    }}
                    isError={flow.error === 'wrong-code'}
                  />
                )}
                {!flow.preparation && (
                  <Checkbox
                    isActive={accepted}
                    onPress={() => setAccepted((value) => !value)}
                    appearance={{
                      size: 'large',
                      isDanger: true,
                      textStyle: styles.checkboxText,
                      checkboxStyle: styles.checkboxBox,
                    }}
                    style={styles.checkbox}
                    testID="card-block-consent"
                  >
                    {t('consent')}
                  </Checkbox>
                )}
                {flow.error && <GText style={styles.status}>{t(flow.error)}</GText>}
                <Button
                  theme="danger"
                  disabled={flow.isLoading || (flow.preparation ? code.length !== codeLength : !accepted)}
                  isLoading={flow.isLoading}
                  testID="card-block-confirm"
                  onPress={() => {
                    if (flow.preparation) void flow.confirm(code)
                    else void flow.prepare()
                  }}
                >
                  {t('block')}
                </Button>
              </Column>
            </Column>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  )
}
